import React, { useState, useEffect } from 'react';
import { FaHourglassHalf, FaTicketAlt, FaEye, FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaBullseye, FaMoneyBillWave, FaVideo, FaImage, FaLink, FaSearch, FaCopy } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DescriptionViewer from '../../Common/DescriptionViewer';

const UnderReviewAdvertisement = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAd, setSelectedAd] = useState(null); // State for Details Modal
  const [searchTerm, setSearchTerm] = useState(''); // State for Searching by ID
  const [startDateFilter, setStartDateFilter] = useState('');
  const [endDateFilter, setEndDateFilter] = useState('');
  const rowsPerPage = 50;

  useEffect(() => {
    const fetchAds = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/advertiser/advertisements?status=pending`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );

        const data = response.data.data || response.data;
        console.log('Fetched under review advertisements:', data);
        const underReviewAds = Array.isArray(data)
          ? data.filter((ad) => ad.status === 'pending')
          : [];

        setAdvertisements(underReviewAds);
        setPendingCount(underReviewAds.length);
        setCurrentPage(1); // Reset to first page on new data
      } catch (error) {
        console.error('Error fetching under review advertisements:', error);
        toast.error('Failed to load under review advertisements');
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, []);

  // Search & Pagination logic
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDateFilter, endDateFilter]);

  const filteredAds = advertisements.filter(ad => {
    // ID Search matching
    let matchesSearch = true;
    if (searchTerm) {
      const searchStr = searchTerm.toLowerCase();
      matchesSearch = ad._id?.toLowerCase().includes(searchStr) ||
        ad.adId?.toLowerCase().includes(searchStr);
    }

    // Date range matching (using createdAt)
    let matchesDate = true;
    if (startDateFilter || endDateFilter) {
      const adDate = new Date(ad.createdAt);
      adDate.setHours(0, 0, 0, 0); // normalize to match day boundaries
      if (startDateFilter) {
        const start = new Date(startDateFilter);
        start.setHours(0, 0, 0, 0);
        if (adDate < start) matchesDate = false;
      }
      if (endDateFilter) {
        const end = new Date(endDateFilter);
        end.setHours(0, 0, 0, 0);
        if (adDate > end) matchesDate = false;
      }
    }

    return matchesSearch && matchesDate;
  });

  const totalPages = Math.ceil(filteredAds.length / rowsPerPage);
  const paginatedAds = [...filteredAds].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  return (
    <div className="p-2 sm:p-4 md:p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col mb-6 gap-4">
        <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">Under Review Advertisements</h1>

        {/* Floating Pill Bar for Desktop & Mobile Navigation */}
        <div className="bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/40 border border-slate-100 rounded-[1.5rem] sm:rounded-full p-1.5 sm:p-2 w-full mx-auto pointer-events-auto transition-all">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1.5 sm:gap-2 w-full">
            {[
              { path: '/advertiser/leads-status/underReview', label: 'Under Review', activeColor: 'bg-yellow-500', activeShadow: 'shadow-yellow-500/30', active: true },
              { path: '/advertiser/leads-status/pending', label: 'Approved', activeColor: 'bg-blue-500', activeShadow: 'shadow-blue-500/30' },
              { path: '/advertiser/leads-status/accepted', label: 'Accepted', activeColor: 'bg-teal-500', activeShadow: 'shadow-teal-500/30' },
              { path: '/advertiser/leads-status/running', label: 'Running', activeColor: 'bg-emerald-500', activeShadow: 'shadow-emerald-500/30' },
              { path: '/advertiser/leads-status/completed', label: 'Completed', activeColor: 'bg-indigo-500', activeShadow: 'shadow-indigo-500/30' },
              { path: '/advertiser/leads-status/rejected', label: 'Rejected', activeColor: 'bg-rose-500', activeShadow: 'shadow-rose-500/30' }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className={`
                  flex-1 sm:flex-none flex items-center justify-center px-2 sm:px-4 py-2 sm:py-2.5 
                  rounded-xl sm:rounded-full transition-all duration-300 text-[10px] sm:text-xs font-black uppercase tracking-widest text-center whitespace-nowrap
                  ${item.active
                    ? `${item.activeColor} text-white shadow-md ${item.activeShadow} scale-[1.02]`
                    : 'bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700 active:scale-95'
                  }
                `}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mb-4 sm:mb-8">
        <div className="bg-yellow-500 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs sm:text-sm font-medium opacity-80">Under Review Advertisements</p>
              <p className="text-xl sm:text-2xl font-bold mt-1">{pendingCount}</p>
            </div>
            <FaHourglassHalf className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 opacity-80" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : advertisements.length === 0 ? (
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center">
          <FaHourglassHalf className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto text-gray-300 mb-2 sm:mb-3 md:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700 mb-2">No Under Review Advertisements</h3>
          <p className="text-xs sm:text-sm md:text-base text-gray-500">You don't have any under review advertisements at the moment.</p>
        </div>
      ) : (
        <>
          {/* Filters Area */}
          <div className="mb-4 sm:mb-6 flex flex-col lg:flex-row gap-4 w-full">
            {/* Search Bar */}
            <div className="relative flex-grow">
              <input
                type="text"
                placeholder="Search by Advertisement ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-sm sm:text-base shadow-sm font-medium text-slate-700 placeholder:text-slate-400"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            </div>

            {/* Date Filters */}
            <div className="flex gap-2">
              <div className="relative flex-1 md:w-36">
                <input
                  type="date"
                  value={startDateFilter}
                  onChange={(e) => setStartDateFilter(e.target.value)}
                  className="w-full px-3 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-xs sm:text-sm shadow-sm font-medium text-slate-700"
                  title="Filter from Start Date"
                />
              </div>
              <div className="relative flex-1 md:w-36">
                <input
                  type="date"
                  value={endDateFilter}
                  onChange={(e) => setEndDateFilter(e.target.value)}
                  className="w-full px-3 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-xs sm:text-sm shadow-sm font-medium text-slate-700"
                  title="Filter to End Date"
                />
              </div>

              {/* Clear Filters Button */}
              {(startDateFilter || endDateFilter) && (
                <button
                  onClick={() => {
                    setStartDateFilter('');
                    setEndDateFilter('');
                  }}
                  className="flex items-center justify-center px-3 py-3 sm:py-3.5 rounded-2xl border-2 border-rose-100 bg-rose-50 text-rose-500 hover:bg-rose-100 hover:text-rose-600 transition-all"
                  title="Clear Date Filters"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          </div>
          {/* Mobile/Tablet Compact Card View (Hidden on LG and up) */}
          <div className="lg:hidden flex flex-col gap-3 mb-4">
            {paginatedAds.map((ad) => (
              <div key={ad._id} className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 flex gap-3 relative hover:shadow-md transition-shadow">
                {/* Compact Left Side Media */}
                <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0 bg-slate-50 border border-slate-100 shadow-inner flex items-center justify-center relative group">
                  {ad.mediaType === 'image' ? (
                    <img src={ad.imageUrl} alt={ad.title} className="w-full h-full object-cover" />
                  ) : (
                    <video src={ad.videoUrl} className="w-full h-full object-cover" />
                  )}
                  <div className="absolute top-1 left-1 bg-black/60 backdrop-blur-sm text-white text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">
                    {ad.mediaType}
                  </div>
                </div>

                {/* Right Side Content */}
                <div className="flex-1 min-w-0 pr-1 flex flex-col justify-center">
                  <div className="flex items-start justify-between gap-1 mb-1">
                    <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-md w-max group/id">
                      <p className="text-[10px] font-black text-slate-400 font-mono tracking-tighter truncate uppercase">#{ad._id.substring(ad._id.length - 8)}</p>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(ad._id);
                          toast.success('ID Copied!');
                        }}
                        className="text-slate-400 hover:text-indigo-600 transition-colors p-0.5"
                        title="Copy ID"
                      >
                        <FaCopy className="w-2.5 h-2.5 group-hover/id:scale-110 transition-transform" />
                      </button>
                    </div>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setSelectedAd(ad)} className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-indigo-50 hover:text-indigo-600 transition-colors">
                        <FaEye className="w-3 h-3" />
                      </button>
                      <span className="shrink-0 bg-yellow-50 text-yellow-600 text-[8px] sm:text-[9px] font-black px-2 py-1 rounded-md uppercase tracking-widest leading-none border border-yellow-100 flex items-center gap-1">
                        <FaHourglassHalf className="w-2 h-2" /> Review
                      </span>
                    </div>
                  </div>

                  <p className="text-xs font-bold text-slate-800 line-clamp-2 leading-tight mb-2">
                    {ad.mediaDescription || "No description provided."}
                  </p>

                  <div className="flex flex-wrap gap-1.5 items-center">
                    <span className="text-[10px] font-black bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-lg border border-emerald-100">
                      ₹{Number(ad.totalCost).toFixed(2)}
                    </span>
                    <span className="text-[10px] font-bold bg-slate-50 text-slate-600 px-2 py-0.5 rounded-lg border border-slate-100 capitalize flex items-center gap-1">
                      {ad.userType === 'influencer' ? '🎯' : '📰'} {ad.userType}
                    </span>
                    <span className="text-[10px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-lg border border-blue-100 capitalize">
                      {ad.requiredViews} Views
                    </span>
                    <span className="text-[9px] font-bold bg-gray-50 text-gray-500 px-2 py-0.5 rounded-lg border border-gray-100">
                      {new Date(ad.createdAt).toLocaleDateString()} {new Date(ad.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>

                  {ad.couponCode && (
                    <div className="mt-1.5 flex items-center gap-1">
                      <FaTicketAlt className="text-[9px] text-orange-400" />
                      <span className="text-[8px] font-black text-orange-600 uppercase tracking-widest">{ad.couponCode} applied</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View (Hidden below LG) */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 text-xs sm:text-sm">
              <thead>
                <tr className="bg-yellow-100 text-yellow-800">
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">S.No.</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Media</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Ad ID</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Description</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Budget</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Ad Type</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Target User Type</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Coupon Code</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">View Detail</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Views</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Location</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Date</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">Status</th>
                  <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 text-left text-[10px] sm:text-xs font-medium">CreatedAt</th>
                </tr>
              </thead>
              <tbody>
                {paginatedAds.map((ad, index) => (
                  <tr key={ad._id} className="border-b">
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-[10px] sm:text-xs font-medium text-slate-500 whitespace-nowrap">
                      {(currentPage - 1) * rowsPerPage + index + 1}
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      {ad.mediaType === 'image' ? (
                        <img src={ad.imageUrl} alt={ad.title} className="w-16 h-12 sm:w-20 sm:h-14 md:w-24 md:h-16 object-contain rounded" />
                      ) : (
                        <video src={ad.videoUrl} controls className="w-16 h-12 sm:w-20 sm:h-14 md:w-24 md:h-16 object-contain rounded" />
                      )}
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-md w-max group/id">
                        <span className="font-semibold text-[10px] sm:text-xs md:text-sm truncate max-w-[80px] sm:max-w-[120px] md:max-w-none">{ad._id}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(ad._id);
                            toast.success('ID Copied!');
                          }}
                          className="text-slate-400 hover:text-indigo-600 transition-colors p-0.5"
                          title="Copy ID"
                        >
                          <FaCopy className="w-3 h-3 group-hover/id:scale-110 transition-transform" />
                        </button>
                      </div>
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      <DescriptionViewer
                        description={ad.mediaDescription}
                        wordLimit={5}
                        textSize="text-[10px] sm:text-xs md:text-sm"
                      />
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-[10px] sm:text-xs md:text-sm">₹{Number(ad.totalCost).toFixed(2)}</td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-[10px] sm:text-xs md:text-sm">{ad.adType}</td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      <span className={`inline-flex items-center px-1.5 sm:px-2 md:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${ad.userType === 'influencer'
                        ? 'bg-purple-100 text-purple-800'
                        : 'bg-blue-100 text-blue-800'
                        }`}>
                        <span className="hidden sm:inline">{ad.userType === 'influencer' ? '🎯 Influencer' : '📰 Reporter'}</span>
                        <span className="sm:hidden">{ad.userType === 'influencer' ? '🎯' : '📰'}</span>
                      </span>
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      {ad.couponCode ? (
                        <span className="inline-flex items-center px-1.5 sm:px-2 md:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-orange-100 text-orange-800 gap-1">
                          <FaTicketAlt className="w-2 h-2 sm:w-3 sm:h-3" />
                          <span className="truncate max-w-[60px] sm:max-w-none">{ad.couponCode}</span>
                        </span>
                      ) : (
                        <span className="text-gray-400 text-[10px] sm:text-xs">N/A</span>
                      )}
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-center text-[10px] sm:text-xs">
                      <button onClick={() => setSelectedAd(ad)} className="bg-indigo-50 text-indigo-600 p-2 rounded-lg border border-indigo-100 hover:bg-indigo-100 hover:shadow-sm transition-all shadow-indigo-200/50 flex items-center justify-center mx-auto">
                        <FaEye className="w-4 h-4" />
                      </button>
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-[10px] sm:text-xs md:text-sm">{ad.requiredViews}</td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      <div className="text-[10px] sm:text-xs md:text-sm font-medium text-gray-900">
                        {ad.pfState || "Not specified"}
                      </div>
                      <div className="text-[10px] sm:text-xs text-gray-500">
                        {ad.pfCities && ad.pfCities.length > 0 ? ad.pfCities.join(", ") : "No cities"}
                      </div>
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-[10px] sm:text-xs">
                      {ad.startDate ? (
                        <>
                          <span className="font-bold"></span> {ad.startDate} to {ad.endDate}
                        </>
                      ) : (
                        <span className="text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2">
                      <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-100 text-yellow-800 rounded-full text-[10px] sm:text-xs font-medium flex items-center w-max">
                        <FaHourglassHalf className="mr-0.5 sm:mr-1 w-2 h-2 sm:w-3 sm:h-3" />
                        <span className="hidden sm:inline">Under Review</span>
                        <span className="sm:hidden">Review</span>
                      </span>
                    </td>
                    <td className="px-1.5 sm:px-2 md:px-4 py-1 sm:py-2 text-[10px] sm:text-xs text-gray-500">
                      {new Date(ad.createdAt).toLocaleDateString('en-IN', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                      <br />
                      {new Date(ad.createdAt).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Show empty state if nothing matches search but list is not entirely empty overall */}
          {advertisements.length > 0 && filteredAds.length === 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center text-slate-500 mt-4">
              No advertisements found matching "{searchTerm}"
            </div>
          )}

          {/* Pagination Controls */}
          {filteredAds.length > 0 && totalPages > 1 && (
            <div className="flex justify-center mt-3 sm:mt-6 px-2 sm:px-4">
              <nav className="inline-flex items-center space-x-1 sm:space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm md:text-base ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Prev
                </button>
                <span className="px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 bg-yellow-300 text-yellow-900 font-bold text-xs sm:text-sm md:text-base">
                  {currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm md:text-base ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  Next
                </button>
              </nav>
            </div>
          )}

          {/* Full Details Modal - Complete Information Rendering */}
          {selectedAd && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
              <div
                className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity"
                onClick={() => setSelectedAd(null)}
              ></div>
              <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] animate-slide-up">

                {/* Modal Header */}
                <div className="flex justify-between items-center p-4 sm:p-6 border-b border-slate-100 bg-slate-50/50">
                  <div>
                    <h2 className="text-xl font-black text-slate-800 tracking-tight">Campaign Details</h2>
                    <p className="text-xs font-bold text-slate-400 font-mono tracking-tighter mt-0.5">ID: {selectedAd._id}</p>
                  </div>
                  <button
                    onClick={() => setSelectedAd(null)}
                    className="p-2 bg-white text-slate-400 hover:text-slate-800 rounded-full shadow-sm border border-slate-100 transition-colors"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Layout - Scrollable Body */}
                <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex flex-col gap-6">

                  {/* Top Status Banner */}
                  <div className="flex flex-wrap md:flex-nowrap gap-3 items-center justify-between p-4 bg-yellow-50 rounded-2xl border border-yellow-100 w-full">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <FaHourglassHalf className="text-yellow-600 text-lg" />
                      </div>
                      <div>
                        <p className="text-sm font-black text-yellow-800 tracking-tight">Under Review</p>
                        <p className="text-[10px] text-yellow-600 font-bold uppercase tracking-widest">Pending Admin Approval</p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-[10px] text-yellow-600 font-bold uppercase tracking-widest">Created On</p>
                      <p className="text-xs font-black text-yellow-800">{new Date(selectedAd.createdAt).toLocaleDateString()} at {new Date(selectedAd.createdAt).toLocaleTimeString()}</p>
                    </div>
                  </div>

                  {/* Two Column Layout for details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                    {/* Left Column: Media & Core Details */}
                    <div className="space-y-6">
                      {/* Media Preview */}
                      <div>
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                          {selectedAd.mediaType === 'image' ? <FaImage /> : <FaVideo />} Ad Media ({selectedAd.mediaType})
                        </h3>
                        <div className="w-full bg-slate-900 rounded-2xl overflow-hidden aspect-video relative shadow-inner border border-slate-200">
                          {selectedAd.mediaType === 'image' ? (
                            <img src={selectedAd.imageUrl} alt={selectedAd.title} className="w-full h-full object-contain" />
                          ) : (
                            <video src={selectedAd.videoUrl} controls className="w-full h-full object-contain" />
                          )}
                        </div>
                      </div>

                      {/* Description Block */}
                      <div>
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 border-b border-slate-100 pb-2">
                          Description Provided
                        </h3>
                        <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100">
                          <p className="text-sm text-slate-700 font-medium whitespace-pre-wrap">{selectedAd.mediaDescription || "No description provided."}</p>
                        </div>
                      </div>
                    </div>

                    {/* Right Column: Deep Stats */}
                    <div className="space-y-6">

                      {/* Targets & Finance */}
                      <div>
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                          <FaBullseye /> Campaign Targets
                        </h3>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="p-3 bg-blue-50 border border-blue-100 rounded-2xl">
                            <p className="text-[9px] font-bold text-blue-500 uppercase tracking-widest">Target Users</p>
                            <p className="text-sm font-black text-blue-700 capitalize flex items-center gap-1 mt-0.5">
                              {selectedAd.userType === 'influencer' ? '🎯' : '📰'} {selectedAd.userType}s
                            </p>
                          </div>
                          <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-2xl">
                            <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest">Total Budget Cost</p>
                            <p className="text-sm font-black text-emerald-700 flex items-center gap-1 mt-0.5">
                              ₹{Number(selectedAd.totalCost).toFixed(2)}
                            </p>
                          </div>
                          <div className="p-3 bg-violet-50 border border-violet-100 rounded-2xl">
                            <p className="text-[9px] font-bold text-violet-500 uppercase tracking-widest">Required Views</p>
                            <p className="text-sm font-black text-violet-700 flex items-center gap-1 mt-0.5">
                              <FaEye className="text-violet-400" /> {selectedAd.requiredViews}
                            </p>
                          </div>
                          <div className="p-3 bg-orange-50 border border-orange-100 rounded-2xl">
                            <p className="text-[9px] font-bold text-orange-500 uppercase tracking-widest">Coupon Used</p>
                            <p className="text-sm font-black text-orange-700 flex items-center gap-1 mt-0.5">
                              {selectedAd.couponCode ? <><FaTicketAlt className="text-orange-400" /> {selectedAd.couponCode}</> : "None"}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Demographics Area */}
                      <div>
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                          <FaMapMarkerAlt /> Geographical Targeting
                        </h3>
                        <div className="p-4 bg-slate-50 border border-slate-100 rounded-2xl space-y-3">
                          <div>
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">State Level</p>
                            <span className="inline-block px-3 py-1 bg-white border border-slate-200 shadow-sm rounded-lg text-xs font-bold text-slate-700">
                              {selectedAd.pfState || "All States"}
                            </span>
                          </div>
                          <div>
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Specific Cities</p>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedAd.pfCities && selectedAd.pfCities.length > 0 ? (
                                selectedAd.pfCities.map(city => (
                                  <span key={city} className="inline-block px-2.5 py-0.5 bg-white border border-slate-200 shadow-sm rounded-md text-[10px] font-black text-slate-600">
                                    {city}
                                  </span>
                                ))
                              ) : (
                                <span className="text-xs font-semibold text-slate-400">No specific cities</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Timelines */}
                      <div>
                        <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-2">
                          <FaCalendarAlt /> Campaign Schedule
                        </h3>
                        <div className="flex gap-4">
                          <div className="flex-1 p-3 bg-slate-50 border border-slate-100 rounded-2xl text-center">
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">Start Date</p>
                            <p className="text-xs font-black text-slate-800">{selectedAd.startDate || "N/A"}</p>
                          </div>
                          <div className="flex-1 p-3 bg-slate-50 border border-slate-100 rounded-2xl text-center">
                            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-1">End Date</p>
                            <p className="text-xs font-black text-slate-800">{selectedAd.endDate || "N/A"}</p>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>

                  {/* Any extra optional URL attachments */}
                  {selectedAd.targetUrl && (
                    <div className="p-4 bg-blue-50 rounded-2xl border border-blue-100 flex items-center justify-between gap-4 mt-2">
                      <div className="flex items-center gap-3 w-full">
                        <FaLink className="text-blue-500 text-xl shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] font-bold text-blue-500 uppercase tracking-widest mb-0.5">Attached Target Link</p>
                          <a href={selectedAd.targetUrl} target="_blank" rel="noreferrer" className="text-sm font-black text-blue-700 hover:underline truncate block w-full">
                            {selectedAd.targetUrl}
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UnderReviewAdvertisement;

























